import * as XLSX from 'xlsx';

/**
 * Utility function to export data to Excel with proper formatting
 * @param {Array} data - Array of objects to export
 * @param {string} filename - Name of the file (without extension)
 * @param {string} sheetName - Name of the worksheet
 * @param {Object} options - Additional options for formatting
 */
export const exportToExcel = (data, filename, sheetName = 'Sheet1', options = {}) => {
  try {
    if (!data || data.length === 0) {
      alert('⚠️ No data available to export');
      return;
    }

    // Clean data - replace null/undefined with empty strings
    const cleanData = data.map(row => {
      const cleanRow = {};
      Object.keys(row).forEach(key => {
        cleanRow[key] = row[key] ?? '';
      });
      return cleanRow;
    });

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(cleanData);
    
    // Set column widths for better formatting
    const colWidths = Object.keys(cleanData[0] || {}).map(key => ({
      wch: Math.max(key.length, options.minColumnWidth || 12, 
        Math.min(options.maxColumnWidth || 50, 
          Math.max(...cleanData.map(row => String(row[key] || '').length))
        )
      )
    }));
    ws['!cols'] = colWidths;
    
    // Add header styling if requested
    if (options.headerStyle !== false) {
      const headerRange = XLSX.utils.decode_range(ws['!ref']);
      for (let col = headerRange.s.c; col <= headerRange.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
        if (!ws[cellAddress]) continue;
        
        ws[cellAddress].s = {
          font: { bold: true, color: { rgb: "FFFFFF" } },
          fill: { fgColor: { rgb: "366092" } },
          alignment: { horizontal: "center", vertical: "center" }
        };
      }
    }
    
    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    
    // Generate filename with timestamp
    const timestamp = new Date().toISOString().split('T')[0];
    const fullFilename = `${filename}_${timestamp}.xlsx`;
    
    // Write and download file
    XLSX.writeFile(wb, fullFilename);
    
    // Show success message
    alert(`✅ Excel file exported successfully as ${fullFilename}`);
    
    return true;
  } catch (error) {
    console.error('Error exporting to Excel:', error);
    alert('❌ Error exporting to Excel: ' + error.message);
    return false;
  }
};

/**
 * Format family data for Excel export
 * @param {Array} families - Array of family objects
 * @returns {Array} Formatted data for Excel
 */
export const formatFamilyDataForExcel = (families) => {
  return families.map((family, index) => ({
    'Sr. No.': index + 1,
    'Family ID': family.id || '',
    'Head Name': family.basic?.headName || '',
    'Display Name': family.basic?.displayName || '',
    'Mobile': family.basic?.mobile || family.meta?.contactNumber || '',
    'State': family.basic?.state || '',
    'City': family.basic?.city || '',
    'Block': family.basic?.block || '',
    'Area Type': family.basic?.areaType || '',
    'Address': family.basic?.address || '',
    'Panchayat': family.basic?.panchayat || '',
    'Gram': family.basic?.gram || '',
    'Urban Area': family.basic?.urbanArea || '',
    'Total Members': family.basic?.Totalmember || Object.keys(family.members || {}).length || 0,
    'Mandir': family.basic?.Mandirtype || '',
    'Mandir Relation': family.basic?.mandirRelation || '',
    'Language': family.basic?.language || '',
    'Family Type': family.basic?.familyType || '',
    'House Type': family.basic?.houseType || '',
    'Jati Prakar': family.basic?.jatiPrakar || '',
    'Upjati Prakar': family.basic?.upjatiPrakar || '',
    'Gotra': family.basic?.gotra || '',
    'Moor': family.basic?.moor || '',
    'Work Type': family.basic?.workType || '',
    'Work Area': family.basic?.workArea || '',
    'Status': family.meta?.status || 'pending',
    'Registration Date': family.meta?.createdAt ? new Date(family.meta.createdAt).toLocaleDateString('en-IN') : '',
    'Approved Date': family.meta?.approvedAt ? new Date(family.meta.approvedAt).toLocaleDateString('en-IN') : '',
    'Created At': family.meta?.createdAt ? new Date(family.meta.createdAt).toLocaleString('en-IN') : ''
  }));
};

/**
 * Format member data for Excel export
 * @param {Array} members - Array of member objects (flattened from families)
 * @returns {Array} Formatted data for Excel
 */
export const formatMemberDataForExcel = (members) => {
  return members.map((member, index) => ({
    'Sr. No.': index + 1,
    'Family ID': member.familyId || '',
    'Member Name': member.name || '',
    'Family Head': member.headName || '',
    'Popular Name': member.familyPopularName || '',
    'Father/Husband': member.fatherName || '',
    'Relation': member.relation || '',
    'Gender': member.gender || '',
    'Member Mobile': member.mobile || '',
    'Family Mobile': member.familyMobile || '',
    'WhatsApp': member.whatsapp || '',
    'Email': member.email || '',
    'DOB': member.dob || '',
    'Age': member.dob ? Math.floor((new Date() - new Date(member.dob)) / (365.25 * 24 * 60 * 60 * 1000)) : '',
    'Aadhaar': member.aadhaar || '',
    'Blood Group': member.bloodGroup || '',
    'Marital Status': member.maritalStatus || '',
    'Country': 'India',
    'State': member.familyState || '',
    'City': member.familyCity || '',
    'Block': member.familyBlock || '',
    'Area Type': member.familyAreaType || '',
    'Panchayat': member.familyPanchayat || '',
    'Gram': member.familyGram || '',
    'Urban Area': member.familyUrbanArea || '',
    'Family Address': member.familyAddress || '',
    'Current Address': member.currentAddress || '',
    'Birth Address': member.birthAddress || '',
    'Education Status': member.educationStatus || '',
    'Education State': member.educationState || '',
    'Education District': member.educationDistrict || '',
    'Education Class': member.educationClass || '',
    'Education Subject': member.educationSubject || '',
    'Work Type': member.workType || '',
    'Work State': member.workState || '',
    'Work District': member.workDistrict || '',
    'Work Area': member.workArea || '',
    'Work Area Name': member.workAreaName || '',
    'Work Department': member.workDepartment || '',
    'Work Position': member.workPosition || '',
    'Family Status': member.familyStatus || '',
    'Registration Date': member.registrationDate ? new Date(member.registrationDate).toLocaleDateString('en-IN') : ''
  }));
};